import { type FC, useState } from "react";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import Tooltip from "@mui/material/Tooltip";
import DownloadIcon from "@mui/icons-material/Download";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/shared/hooks";
import { useCreateReportMutation } from "@/partner/entities/reports";
import type {
  AnalyticsWidgetType,
  ReportTypeType,
} from "@/types/graphql/partner.generated";
import { ReportProgressDialog } from "./ReportProgressDialog";

interface DownloadReportButtonProps {
  type: ReportTypeType;
  widget?: AnalyticsWidgetType;
  /** Фильтр страницы в том виде, в каком он уходит в запрос списка. */
  filter: Record<string, unknown> | undefined;
  /** Дополнительные переменные запроса виджета: calendarInterval, dimension. */
  variables?: Record<string, unknown>;
  tooltip?: string;
  size?: "small" | "medium";
  /** filter: слот Filter.action; card: шапка карточки аналитики. */
  variant?: "filter" | "card";
}

export const DownloadReportButton: FC<DownloadReportButtonProps> = ({
  type,
  widget,
  filter,
  variables,
  tooltip,
  size,
  variant = "filter",
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useIsMobile("md");
  const [reportId, setReportId] = useState<string | null>(null);

  const [createReport, { loading }] = useCreateReportMutation({
    onCompleted: (data) => {
      setReportId(data.createReport.id);
      enqueueSnackbar(t("reports.button.created"), { variant: "success" });
    },
    onError: (error) => {
      const message =
        error.message === "TOO_MANY_ACTIVE_REPORTS"
          ? t("reports.error.tooManyActive")
          : error.message;
      enqueueSnackbar(message, { variant: "error" });
    },
  });

  const handleClick = () =>
    createReport({
      variables: {
        input: {
          type,
          widget,
          filter: filter ?? {},
          variables: variables ?? {},
        },
      },
    });

  return (
    <>
      <Tooltip title={tooltip ?? t("reports.button.tooltip")}>
        <Stack component="span">
          {variant === "card" ? (
            <ToggleButton
              value="download"
              size="small"
              disabled={loading}
              onClick={handleClick}
            >
              <DownloadIcon />
            </ToggleButton>
          ) : (
            <IconButton
              size={size ?? (isMobile ? "small" : "medium")}
              disabled={loading}
              onClick={handleClick}
            >
              <DownloadIcon />
            </IconButton>
          )}
        </Stack>
      </Tooltip>
      {reportId && (
        <ReportProgressDialog
          reportId={reportId}
          onClose={() => setReportId(null)}
        />
      )}
    </>
  );
};
